import * as React from 'react';
import { XYCoord } from 'dnd-core';
import {
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
} from 'react-dnd';
import { Tooltip } from '../../../components';
import { Menu_menu_categories_items as MenuItem } from './__generated__/Menu';
import { FeaturedStar } from './FeaturedStar';
const classNames = require('classnames');

interface MenuTileBaseProps {
  menuItem: MenuItem;
  menuOpen: boolean;

  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;

  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;

  onToggleEnabled(item: MenuItem): void;
  onDeleteItem(item: MenuItem): void;
  onSelectItem(item: MenuItem): void;
  onToggleFeatured(item: MenuItem): void;
  copyMenuItem(item: MenuItem): void;
  openMenu(item: MenuItem): void;
  hideMenu(): void;
}

interface MenuTileProps extends MenuTileBaseProps {
  innerRef: any;
}

export class MenuTileUnwrapped extends React.Component<MenuTileProps, {}> {
  openMenu = () => {
    this.props.openMenu(this.props.menuItem);
  };

  goToItem = () => {
    this.props.onSelectItem(this.props.menuItem);
  };

  toggleMenuItem = () => {
    this.props.onToggleEnabled(this.props.menuItem);
  };

  copyMenuItem = () => {
    this.props.copyMenuItem(this.props.menuItem);
  };

  deleteMenuItem = () => {
    this.props.onDeleteItem(this.props.menuItem);
  };

  toggleFeatured = (e) => {
    e.stopPropagation();
    this.props.onToggleFeatured(this.props.menuItem);
  };

  render() {
    const { connectDragSource, connectDropTarget, menuItem } = this.props;
    const { item } = menuItem;

    const classes = classNames('menu-tile', 'menu-tile-item', {
      disabled: !menuItem.enabled,
    });
    connectDragSource(this.props.innerRef);
    connectDropTarget(this.props.innerRef);

    return (
      <div>
        <div ref={this.props.innerRef} role='button' onClick={this.goToItem} className={classes}>
          <div className='d-flex justify-content-between w-100'>
            <p className='menu-tile-title'>
              {item.name}
              {!menuItem.enabled && <span className='menu-tile-title-disabled'>INACTIVE</span>}
            </p>
          </div>
          <p className='menu-tile-price'>${menuItem.price}</p>
          <FeaturedStar className='menu-tile-featured' selected={menuItem.featured} onClick={this.toggleFeatured} />
          <Tooltip openMenu={this.openMenu} hideMenu={this.props.hideMenu} open={this.props.menuOpen}>
            <Tooltip.Action onClick={this.toggleMenuItem}>{!menuItem.enabled ? 'Enable' : 'Disable'}</Tooltip.Action>
            <Tooltip.Action onClick={this.copyMenuItem}>Copy item</Tooltip.Action>
            <Tooltip.Action onClick={this.deleteMenuItem}>Delete</Tooltip.Action>
          </Tooltip>
        </div>
        {this.props.menuOpen && (
          <Tooltip.MobileMenu>
            <Tooltip.MobileAction onClick={this.toggleMenuItem}>
              <div>{!menuItem.enabled ? 'Enable' : 'Disable'}</div>
            </Tooltip.MobileAction>
            <Tooltip.MobileAction onClick={this.deleteMenuItem}>
              <div>Delete</div>
            </Tooltip.MobileAction>
          </Tooltip.MobileMenu>
        )}
      </div>
    );
  }
}

const MenuTileWithRef = React.forwardRef((props: MenuTileBaseProps, ref) => (
  <MenuTileUnwrapped {...props} innerRef={ref} />
));

// export const MenuTile = dragTarget(dragSource(MenuTileUnwrapped));
export const MenuTile = DropTarget(
  'MenuTile',
  {
    hover(props: MenuTileBaseProps, monitor: DropTargetMonitor, node: HTMLDivElement | null) {
      if (!node) {
        return null;
      }

      const dragIndex = monitor.getItem().index;
      const hoverIndex = props.index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = node.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      props.moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      monitor.getItem().index = hoverIndex;
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    'MenuTile',
    {
      beginDrag: (props: MenuTileBaseProps) => ({
        index: props.index,
      }),
    },
    (connect: DragSourceConnector) => ({
      connectDragSource: connect.dragSource(),
    })
  )(MenuTileWithRef)
);
